import { PageHeader } from '@keplerco/core';
import React from 'react';
import { useParams } from 'react-router';
import { EmptyState } from '../../components/general/empty-state/empty-state';
import { PageSkeletonLoader } from '../../components/general/loading-state/loader-layers/page-loader-layer/page-skeleton-loader';
import { OrganizationLevelType } from '../../enums';
import { AssessmentSubType, AssessmentType } from '../../enums/assessment-type';
import { ScoreComparison } from '../../enums/score-comparison';
import { PermissionSearchParams, AssessmentSearchParams } from '../../models/overmind/search-params';
import { PagePath } from '../../navigation/navigation.enums';
import { useAppActions, useAppState } from '../../overmind';
import AssessmentScoreSkillWidget from '../../widgets/analytics/assessment-score-skill/assessment-score-skill.widget';
import { ScoreOverviewWidget } from '../../widgets/analytics/score-overview/score-overview.widget';
import { SunburstWidget } from '../../widgets/analytics/sunburst/sunburst.widget';
import { FrequencyUseWidget } from '../analytics/assessments/versus-score/frequency-use.widget';
import { YearsExperienceWidget } from '../analytics/assessments/versus-score/years-experience.widget';
import analyticsStyles from '../analytics/analytics.module.css';
import { getSeniorityDescription } from '../../library/helpers/get-seniority-description';
import { AssessmentResponse } from '../../overmind/actions';
import { SkillSpecificFeedbackWidget } from '../../widgets/analytics/feedback/skill-specific-feedback.widget';
import { OverallFeedbackWidget } from '../../widgets/analytics/feedback/overall-feedback.widget';
import { AnalyticsFiltersOptions } from '../analytics/assessments/assessment/assessment.models';
import AssessmentRatingsWidget from '../../widgets/analytics/assessment-ratings/assessment-ratings.widget';

export function YourAssessmentPage(): React.ReactElement {
  const { assessmentSlug } = useParams();

  const actions = useAppActions();
  const { user, companyVariables } = useAppState();

  const [isLoading, setIsLoading] = React.useState(true);
  const [assessment, setAssessment] = React.useState<AssessmentResponse>();
  const [pathways, setPathways] = React.useState<{ name: string; seniority: string }[]>([]);
  const [filtersOptions, setFiltersOptions] = React.useState<AnalyticsFiltersOptions>({
    departmentOptions: [],
    teamOptions: [],
    roleOptions: [],
    skillOptions: [],
    countryOptions: [],
    subjectsOptions: [],
    assessorsOptions: [],
  });
  const [isAnonymous, setIsAnonymous] = React.useState<boolean>(false);

  const permissionSearchParams: PermissionSearchParams | undefined = React.useMemo(() => {
    if (!companyVariables.slug || !user?.learnerSlug) return undefined;

    return {
      organizationLevel: OrganizationLevelType.Learner,
      companySlug: companyVariables.slug,
      departmentSlug: undefined,
      teamSlug: undefined,
      learnerSlug: user?.learnerSlug,
    };
  }, [companyVariables.slug, user?.learnerSlug]);

  const assessmentSearchParams: AssessmentSearchParams | undefined = React.useMemo(() => {
    if (!assessment || !companyVariables.slug) return undefined;

    return {
      assessmentSubType: assessment.assessmentSubType,
      assessmentSlug: assessment.slug,
    };
  }, [assessment]);

  React.useEffect(() => {
    async function getData() {
      setIsLoading(true);
      const response = await actions.getAssessment(assessmentSlug!);
      setAssessment(response);

      if (assessment?.assessmentType === AssessmentType.FieldsOfPractice && !!user?.learnerSlug) {
        const pathwaysResponse = await actions.getLearnerCareerPaths({
          assessmentSlug: assessmentSlug!,
          companySlug: companyVariables.slug!,
          learnerSlug: user.learnerSlug!,
        });

        setPathways(
          pathwaysResponse?.map(pathway => ({
            name: pathway.careerPathName,
            seniority: getSeniorityDescription(pathway.seniorityLevelEnum),
          })) || []
        );
      }

      if (assessment?.assessmentSubType === AssessmentSubType.RatingAssessment) {
        const assessors = new Map<string, { name: string; slug: string }>();

        const peerResponse = await actions.getPeerAssessment({ assessmentSlug: assessmentSlug!, learnerSlug: user?.learnerSlug });
        setIsAnonymous(!!peerResponse?.isAnonymous);
        if (!peerResponse?.isAnonymous) {
          peerResponse?.assignees.forEach(assignee => {
            assignee.reviewers.forEach(reviewer => {
              assessors.set(reviewer.learnerSlug, { name: reviewer.fullName, slug: reviewer.learnerSlug });
            });
          });
        }

        const skillsResponse = await actions.getAssessmentAnalyticsSkills(assessmentSlug!);

        setFiltersOptions(currentState => ({
          ...currentState,
          assessorsOptions: Array.from(assessors.values()).map(assessor => ({ value: assessor.slug, label: assessor.name })),
          skillOptions: skillsResponse?.map(skill => ({ label: skill.name, value: skill.slug! })) ?? [],
        }));
      }

      setIsLoading(false);
    }

    getData();
  }, [assessmentSlug, assessment?.assessmentType]);

  if (isLoading) return <PageSkeletonLoader />;

  return (
    <div className="wrapper pageWrapper" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <PageHeader
        breadcrumbs={[
          { name: 'Your Assessments', url: PagePath.yourAssessmentsBase },
          { name: assessment?.name ?? 'Your Assessment', url: location.pathname },
        ]}
        title={assessment?.name ?? 'Your Assessment'}
        subtitle={pathways.map(pathway => `${pathway.name}: ${pathway.seniority}`).join(' | ')}
        divider
      />

      {!!permissionSearchParams && !!assessmentSearchParams ? (
        <React.Fragment>
          <ScoreOverviewWidget path={PagePath.yourAssessmentsAssessment} baseSearchParams={{ ...permissionSearchParams, ...assessmentSearchParams }} />

          <SunburstWidget chartTitle="Your score for each skill included in this assessment:" skillScoreTitle="Your score" permissionSearchParams={permissionSearchParams} assessmentSearchParams={assessmentSearchParams} scoreComparison={ScoreComparison.None} />

          <AssessmentScoreSkillWidget baseSearchParams={{ ...permissionSearchParams, ...assessmentSearchParams }} />

          {assessmentSearchParams.assessmentSubType === AssessmentSubType.RatingAssessment && <AssessmentRatingsWidget assessmentSlug={assessmentSearchParams.assessmentSlug} learnerSlug={permissionSearchParams.learnerSlug!} />}

          {assessmentSearchParams.assessmentSubType === AssessmentSubType.GroupAssessment && (
            <div className={analyticsStyles.oneOneGrid}>
              <YearsExperienceWidget title="Years of experience vs score" subtitle="Your score for each skill included in this assessment compared to how many years of experience you have in the skill." baseSearchParams={{ ...permissionSearchParams, ...assessmentSearchParams }} />

              <FrequencyUseWidget title="Frequency of use vs score" subtitle="Your score for each skill included in this assessment compared to how often you use the skill." baseSearchParams={{ ...permissionSearchParams, ...assessmentSearchParams }} />
            </div>
          )}

          {assessmentSearchParams.assessmentSubType === AssessmentSubType.RatingAssessment && (
            <React.Fragment>
              <SkillSpecificFeedbackWidget isAnonymous={isAnonymous} baseSearchParams={{ ...permissionSearchParams, ...assessmentSearchParams }} filtersOptions={filtersOptions} isPerson />
              <OverallFeedbackWidget isAnonymous={isAnonymous} baseSearchParams={{ ...permissionSearchParams, ...assessmentSearchParams }} filtersOptions={{ ...filtersOptions, skillOptions: [] }} isPerson />
            </React.Fragment>
          )}
        </React.Fragment>
      ) : (
        <EmptyState />
      )}
    </div>
  );
}
